import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface HoldToConfirmButtonProps {
  /** Fired once the hold completes. */
  onConfirm: () => void;
  /** How long the button must be held, in ms. Scale it with the blast radius. */
  holdMs: number;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /** Tooltip text. The "hold" instruction is appended for you. */
  title?: string;
  "aria-label"?: string;
  /**
   * Which edge the confirming fill grows from — "bottom" (the default) rises,
   * "left" sweeps rightward across the button. Icon-sized buttons read the
   * rise; a wide labelled button reads the sweep, because progress along the
   * long axis is the axis the eye is already scanning.
   */
  fillFrom?: "bottom" | "left";
}

/** How long the fill sits solid red before the action fires. */
const FLASH_MS = 130;

/**
 * A destructive button that commits only after a press-and-hold.
 *
 * It replaces the confirmation dialog on *recoverable* deletes: a modal for
 * something one click can undo is a speed bump nobody reads, while a hold is
 * impossible to fire by accident and costs no extra screen. Irreversible
 * actions keep their dialog.
 *
 * The visual — a red fill rising from the bottom (or sweeping across from the
 * left, see `fillFrom`), draining if you let go —
 * lives in `.hold-btn` (index.css). Pointer and keyboard both hold: Enter or
 * Space held down runs the same timer, and the synthetic click a button would
 * normally fire from those keys is suppressed, so there is no one-press path
 * to the action.
 */
const HoldToConfirmButton = ({
  onConfirm,
  holdMs,
  children,
  className,
  disabled,
  title,
  "aria-label": ariaLabel,
  fillFrom = "bottom",
}: HoldToConfirmButtonProps) => {
  const [holding, setHolding] = useState(false);
  const [firing, setFiring] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    holdTimer.current = null;
    flashTimer.current = null;
  };

  useEffect(() => clearTimers, []);

  const cancel = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    setHolding(false);
  }, []);

  const start = useCallback(() => {
    if (disabled || holdTimer.current || firing) return;
    setHolding(true);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      setHolding(false);
      setFiring(true);
      flashTimer.current = setTimeout(() => {
        flashTimer.current = null;
        setFiring(false);
        onConfirm();
      }, FLASH_MS);
    }, holdMs);
  }, [disabled, firing, holdMs, onConfirm]);

  // Escape abandons a hold in progress — the same out every other press-and-
  // hold in a UI offers, and the only one available once the pointer is down.
  useEffect(() => {
    if (!holding) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [holding, cancel]);

  return (
    <button
      type="button"
      disabled={disabled}
      data-holding={holding || undefined}
      data-firing={firing || undefined}
      data-fill={fillFrom === "left" ? "left" : undefined}
      title={title ? `${title} (hold to confirm)` : "Hold to confirm"}
      aria-label={ariaLabel}
      style={{ "--hold-ms": `${holdMs}ms` } as CSSProperties}
      className={cn("hold-btn", className)}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.button === 0) start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        // Suppress the click a button fires from these keys: the hold is the
        // only way in.
        e.preventDefault();
        if (!e.repeat) start();
      }}
      onKeyUp={(e) => {
        if (e.key === "Enter" || e.key === " ") cancel();
      }}
      onBlur={cancel}
      onClick={(e) => e.stopPropagation()}
    >
      <span aria-hidden className="hold-btn__fill" />
      <span className="relative flex items-center justify-center gap-2">{children}</span>
    </button>
  );
};

export default HoldToConfirmButton;
